import React, { useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useGuidedTour } from "../../contexts/GuidedTourContext";
import { TOUR_STEPS } from "./tourSteps";
import { useTargetRect, findVisibleElement } from "./useTargetRect";
import TourCallout from "./TourCallout";
import TourCompletionModal from "./TourCompletionModal";
import { useNavigate, useLocation } from "react-router-dom";

const PADDING = 10;

const SkipButton: React.FC<{
  completeTour: () => void;
}> = ({ completeTour }) => (
  <motion.div
    className="fixed z-[10003] top-4 right-4"
    style={{ pointerEvents: "auto" }}
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay: 0.5 }}
  >
    <motion.button
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.97 }}
      className="group flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.3)] border border-white/20 transition-all"
      style={{ background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)", backdropFilter: "blur(12px)" }}
      onClick={completeTour}
    >
      <span className="text-white/80 group-hover:text-white transition-colors">Skip tour</span>
      <svg className="h-3.5 w-3.5 text-white/40 group-hover:text-white/70 group-hover:translate-x-0.5 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
      </svg>
    </motion.button>
  </motion.div>
);

const SpotlightOverlay: React.FC = () => {
  const { tour, isPaused, completeTour, advanceStep } = useGuidedTour();
  const navigate = useNavigate();
  const location = useLocation();

  const step = TOUR_STEPS[tour.currentStep];

  // Detect mobile (matches the md: Tailwind breakpoint)
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  // Apply mobile overrides when on a small screen and overrides are defined
  const effectiveStep = step && isMobile
    ? {
        ...step,
        targetSelector: "mobileTargetSelector" in step ? step.mobileTargetSelector ?? step.targetSelector : step.targetSelector,
        message: step.mobileMessage ?? step.message,
        subMessage: step.mobileSubMessage ?? step.subMessage,
        calloutPosition: step.mobileCalloutPosition ?? step.calloutPosition,
        navigateTo: step.mobileNavigateTo ?? step.navigateTo,
      }
    : step;

  // Resolve selector: static or dynamic
  const selector = effectiveStep
    ? effectiveStep.targetFinder
      ? effectiveStep.targetFinder({
          createdClientId: tour.createdClientId,
          createdMatterId: tour.createdMatterId,
        })
      : effectiveStep.targetSelector
    : null;

  const targetRect = useTargetRect(selector);

  // Lock body scroll when overlay is visible
  useEffect(() => {
    if (!tour.isActive || isPaused) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [tour.isActive, isPaused]);

  // Handle clicks on the spotlight area
  const handleSpotlightClick = useCallback(() => {
    if (!selector) return;

    // For navigation steps, navigate directly (more reliable than .click())
    if (effectiveStep?.navigateTo) {
      navigate(effectiveStep.navigateTo);
      return;
    }

    // For other steps, dispatch a real click event on the target element
    const el = findVisibleElement(selector) as HTMLElement | null;
    if (el) {
      el.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
    }
  }, [selector, effectiveStep, navigate]);

  // Don't render if tour is inactive, user is logged out, or on onboarding page
  if (!tour.isActive || !localStorage.getItem("accessToken") || location.pathname === "/onboarding") return null;

  // Final step: show completion modal
  if (tour.currentStep === TOUR_STEPS.length - 1) {
    return (
      <TourCompletionModal
        onComplete={completeTour}
        clientDisplayName={tour.clientDisplayName}
        matterTitle={tour.matterTitle}
      />
    );
  }

  // Paused during modal interactions  -  still show skip button
  // No target rect yet  -  still show skip button, don't block the UI
  if (isPaused || !targetRect) {
    return ReactDOM.createPortal(
      <SkipButton completeTour={completeTour} />,
      document.body
    );
  }

  // Calculate padded cutout
  const pad = effectiveStep?.spotlightPadding ?? PADDING;
  const r = {
    top: targetRect.top - pad,
    left: targetRect.left - pad,
    width: targetRect.width + pad * 2,
    height: targetRect.height + pad * 2,
  };

  // SVG mask approach: single overlay with a transparent hole
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  return ReactDOM.createPortal(
    <>
      {/* Dark overlay with transparent cutout via SVG */}
      <svg
        className="fixed inset-0 z-[9999]"
        width={vw}
        height={vh}
        style={{ pointerEvents: "none" }}
      >
        <defs>
          <mask id="tour-spotlight-mask">
            {/* White = visible (dark overlay), Black = transparent (cutout hole) */}
            <rect width={vw} height={vh} fill="white" />
            <rect
              x={r.left}
              y={r.top}
              width={r.width}
              height={r.height}
              rx={12}
              ry={12}
              fill="black"
            />
          </mask>
        </defs>
        {/* Dark overlay with hole */}
        <rect
          width={vw}
          height={vh}
          fill="rgba(0,0,0,0.75)"
          mask="url(#tour-spotlight-mask)"
          style={{ pointerEvents: "auto" }}
        />
      </svg>

      {/* Pulsing highlight ring around the target */}
      <div
        className="fixed z-[10000] rounded-xl pointer-events-none"
        style={{
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
          border: "2px solid rgba(255,255,255,0.5)",
          boxShadow:
            "0 0 0 3px rgba(25,135,84,0.5), 0 0 20px rgba(25,135,84,0.3), inset 0 0 20px rgba(255,255,255,0.05)",
          animation: "tour-pulse 2s ease-in-out infinite",
        }}
      />

      {/* Clickable transparent area over the cutout */}
      <div
        className="fixed z-[10001] rounded-xl cursor-pointer"
        style={{
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
          pointerEvents: "auto",
          background: "rgba(255,255,255,0.05)",
        }}
        onClick={handleSpotlightClick}
      />

      {/* Callout tooltip */}
      <div className="fixed inset-0 z-[10002] pointer-events-none">
        <AnimatePresence mode="wait">
          <TourCallout
            key={effectiveStep!.id}
            targetRect={new DOMRect(r.left, r.top, r.width, r.height)}
            step={effectiveStep!}
            stepIndex={tour.currentStep}
          />
        </AnimatePresence>
      </div>

      {/* Skip tour  -  always accessible */}
      <SkipButton completeTour={completeTour} />

      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes tour-pulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(25,135,84,0.5), 0 0 20px rgba(25,135,84,0.3), inset 0 0 20px rgba(255,255,255,0.05); }
          50% { box-shadow: 0 0 0 6px rgba(25,135,84,0.3), 0 0 40px rgba(25,135,84,0.4), inset 0 0 20px rgba(255,255,255,0.1); }
        }
      `}</style>
    </>,
    document.body
  );
};

export default SpotlightOverlay;
